import { usePagination } from '@/shared/ui/navigation/Pagination/hooks/usePagination';
import { PlannerSearchView } from '@/entities/planner';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

export interface UsePlannerPaginationOptions {
  data: PlannerSearchView | null;
  onPageChange?: (cursor: string | null) => void;
  initialPageSize?: number;
  /** 탭 컨텍스트의 params (탭별 독립적인 상태를 위해 사용) */
  tabParams?: Record<string, unknown>;
  /** 탭 params 업데이트 함수 */
  onTabParamsUpdate?: (params: Record<string, unknown>) => void;
}

export interface UsePlannerPaginationReturn {
  cursor: string | null;
  currentPageIndex: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToPrevious: () => void;
  goToNext: () => void;
  setPageSize: (pageSize: number) => void;
  setCursorAndIndex: (cursor: string | null, index: number) => void;
}

/**
 * 플래너 페이지네이션 훅
 * PlannerSearchView와 usePagination을 통합
 */
export function usePlannerPagination({
  data,
  onPageChange,
  initialPageSize = 20,
  tabParams,
  onTabParamsUpdate,
}: UsePlannerPaginationOptions): UsePlannerPaginationReturn {
  const hasNext = data?.hasNext ?? false;
  const nextCursor = data?.nextCursor ?? null;
  const searchParams = useSearchParams();
  const useTabParams = !!tabParams && !!onTabParamsUpdate;
  const pageCursorFromUrl = !useTabParams ? (searchParams?.get('page') ?? null) : null;
  const pageCursorFromTab = useTabParams
    ? ((tabParams['page'] as string | null | undefined) ?? null)
    : null;
  const currentPageCursor = useTabParams ? pageCursorFromTab : pageCursorFromUrl;
  const onPageChangeRef = useRef(onPageChange);
  const lastCursorRef = useRef<string | null>(null);

  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);

  const pagination = usePagination({
    initialCursor: null as string | null,
    pageSize: initialPageSize,
    hasPreviousPage: false,
    hasNextPage: hasNext,
    nextCursor,
    syncWithUrl: !useTabParams,
    queryKey: 'page',
    tabParams,
    onTabParamsUpdate,
    onPageChange: (event) => {
      onPageChangeRef.current?.(event.cursor);
    },
  });

  useEffect(() => {
    if (currentPageCursor !== lastCursorRef.current) {
      lastCursorRef.current = currentPageCursor;
      if (currentPageCursor !== pagination.cursor) {
        onPageChangeRef.current?.(currentPageCursor);
      }
    }
  }, [currentPageCursor, pagination.cursor]);

  return {
    cursor: pagination.cursor,
    currentPageIndex: pagination.currentPageIndex,
    pageSize: pagination.pageSize,
    hasPreviousPage: pagination.hasPreviousPage,
    hasNextPage: pagination.hasNextPage,
    goToPrevious: pagination.goToPrevious,
    goToNext: pagination.goToNext,
    setPageSize: pagination.setPageSize,
    setCursorAndIndex: pagination.setCursorAndIndex,
  };
}
